/**
 * Owns the service-worker lifecycle for the offline-checks feature.
 *
 * Per design §5.1 (and the lessons from PR #1585): the service worker
 * precaches the app shell (HTML/JS/CSS) via CRA's default Workbox config
 * so the page can load while offline. **API responses are NEVER cached
 * by the service worker.** API caching, where it exists in this design,
 * lives in the ETag interceptor (§5.5), not here.
 *
 * Plan issue 3.4 says to gate registration behind `enable_offline_checks`
 * "in index.tsx". We do it from React instead because `useConfig()` is a
 * hook and cannot be called at module load time. This component is the
 * one place that calls `serviceWorker.register()` / `unregister()`, and
 * `index.tsx` no longer touches the SW directly. The deviation is
 * documented in the plan.
 *
 * Behaviour:
 *   - Flag is true: register the SW (idempotent in CRA's wrapper).
 *   - Flag is false: unregister any existing SW.
 *   - Flag flips at runtime (e.g. tenant switch): the effect re-runs and
 *     reconciles. In practice this is rare since the master flag is per
 *     tenant and the tenant-switch invalidation flow (§5.2.1) tends to
 *     reset state first.
 *
 * CRA's `register()` is itself gated to `process.env.NODE_ENV === "production"`
 * — in development it does nothing — so locally this component just calls
 * a no-op. The effect still runs to ensure prod registration is correct
 * once a build ships.
 */

import { useEffect } from "react";
import { useSelector } from "react-redux";

import * as serviceWorker from "../serviceWorker";
import useConfig from "../actions/Tenants/config/configHook";
import { isOfflineChecksEnabled } from "./db";
import { warmETagCache } from "./etagCacheWarmer";
import { setOfflineFeatureEnabled } from "./featureFlag";
import * as layerCache from "./layerCache";

export function OfflineServiceWorker(): null {
    const config = useConfig();
    const enabled = isOfflineChecksEnabled(config);
    const tenant_id = useSelector((state: { auth?: { user?: { tenant?: { tenant_id?: string } } } }) => state?.auth?.user?.tenant?.tenant_id);

    useEffect(() => {
        // Mirror the flag into the module-level cache for code paths that
        // run outside React (axios interceptors, etc.). See ./featureFlag.ts.
        setOfflineFeatureEnabled(enabled);

        if (enabled) {
            serviceWorker.register();
            // Close two startup races where the very first request fires
            // before this effect has mirrored the flag, so the etag and
            // layer-cache writes silently short-circuit:
            //   1. configHook.tsx's [tenant_id] effect calls refresh() too
            //      early — symmetric warm here populates offline-layer-cache.
            //   2. Same effect dispatches configs / field-options / defects
            //      thunks too early — warmETagCache re-fires those URLs so
            //      etag-cache picks up the responses.
            // Both helpers swallow their own errors; floating promises are
            // intentional. See etagInterceptor.ts → warmETagCache.
            layerCache.refresh();
            if (tenant_id) {
                warmETagCache(tenant_id);
            }
        } else {
            serviceWorker.unregister();
        }
    }, [enabled, tenant_id]);

    return null;
}
